import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Search } from 'lucide-react';

/** RFC 4180-ish CSV: quoted fields, doubled quotes, CRLF. Returns rows of strings. */
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;
  const s = String(text || '').replace(/^﻿/, '');
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    if (quoted) {
      if (c === '"' && s[i + 1] === '"') { field += '"'; i += 1; } else if (c === '"') quoted = false; else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') { row.push(field); field = ''; } else if (c === '\n' || c === '\r') {
      if (c === '\r' && s[i + 1] === '\n') i += 1;
      row.push(field); field = '';
      if (row.some((v) => v.trim() !== '')) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some((v) => v.trim() !== '')) rows.push(row);
  return rows;
}

/** A header row of names and data rows of the same width: worth showing as a table, else null. */
export function asTable(text) {
  const rows = parseCsv(text);
  if (rows.length < 2 || rows[0].length < 3) return null;
  const width = rows[0].length;
  const body = rows.slice(1).filter((r) => r.length === width);
  if (body.length < Math.ceil((rows.length - 1) * 0.8)) return null;
  if (rows[0].some((h) => !h.trim() || /^-?[\d.,£$€]+$/.test(h.trim()))) return null;  // first row is data, not names
  return { header: rows[0].map((h) => h.trim()), rows: body };
}

const NAMES = { sku: 'SKU', qty: 'Qty', vat: 'VAT', uom: 'Unit', gtin: 'GTIN', ean: 'EAN', id: 'ID' };
export function label(name) {
  const words = name.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().replace(/[_-]+/g, ' ').trim().split(/\s+/);
  return words.map((w, n) => NAMES[w] || (n === 0 ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');
}

const NUMERIC = /^-?[£$€]?\s?-?\d[\d,]*(\.\d+)?%?$/;
const num = (v) => Number(String(v).replace(/[£$€,%\s]/g, ''));

/** Split columns into the ones every row repeats (shown once above the table) and the ones that vary. */
export function shape({ header, rows }) {
  const cols = header.map((name, i) => {
    const values = rows.map((r) => r[i].trim());
    const distinct = new Set(values);
    const filled = values.filter((v) => v !== '');
    return {
      i, name, label: label(name),
      common: rows.length > 1 && distinct.size === 1 && filled.length === rows.length ? values[0] : null,
      numeric: filled.length > 0 && filled.every((v) => NUMERIC.test(v)),
      choices: distinct.size >= 2 && distinct.size <= 8 && distinct.size < rows.length ? [...distinct].filter(Boolean).sort() : null,
    };
  });
  return { common: cols.filter((c) => c.common !== null), columns: cols.filter((c) => c.common === null) };
}

/** A CSV invoice as a readable table: invoice-level values once, line columns with search, filters and sorting. */
export default function SourceTable({ table }) {
  const { common, columns } = useMemo(() => shape(table), [table]);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState(null);   // { i, dir: 1 | -1 }

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = table.rows.filter((r) => (!q || columns.some((c) => r[c.i].toLowerCase().includes(q)))
      && Object.entries(filters).every(([i, v]) => !v || r[i].trim() === v));
    if (sort) {
      const col = columns.find((c) => c.i === sort.i);
      rows = [...rows].sort((a, b) => sort.dir * (col.numeric
        ? num(a[sort.i]) - num(b[sort.i])
        : a[sort.i].localeCompare(b[sort.i], undefined, { numeric: true, sensitivity: 'base' })));
    }
    return rows;
  }, [table, columns, query, filters, sort]);

  const toggleSort = (i) => setSort((s) => (s && s.i === i ? (s.dir === 1 ? { i, dir: -1 } : null) : { i, dir: 1 }));
  const filterable = columns.filter((c) => c.choices && !c.numeric);

  return (
    <div className="space-y-3 p-2" data-testid="source-table">
      {common.length > 0 && (
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 rounded-lg bg-surface p-3" data-testid="source-table-common">
          {common.map((c) => (
            <div key={c.i}>
              <dt className="text-[11px] uppercase tracking-overline text-ink-muted">{c.label}</dt>
              <dd className="text-sm text-ink">{c.common}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-ink-muted" aria-hidden />
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search lines"
            aria-label="Search lines" data-testid="source-table-search"
            className="w-full rounded-lg border border-surface-border bg-surface pl-8 pr-2 py-1.5 text-sm" />
        </label>
        {filterable.map((c) => (
          <select key={c.i} aria-label={`Filter by ${c.label}`} data-testid={`source-table-filter-${c.name}`}
            value={filters[c.i] || ''} onChange={(e) => setFilters((f) => ({ ...f, [c.i]: e.target.value }))}
            className="rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-sm">
            <option value="">{c.label}: all</option>
            {c.choices.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        ))}
        <span className="text-xs text-ink-muted" data-testid="source-table-count">
          {visible.length === table.rows.length ? `${table.rows.length} lines` : `${visible.length} of ${table.rows.length} lines`}
        </span>
      </div>
      <div className="overflow-auto rounded-lg border border-surface-border bg-surface">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-sunken sticky top-0">
            <tr>
              {columns.map((c) => (
                <th key={c.i} scope="col" className={`px-3 py-2 font-medium text-ink whitespace-nowrap ${c.numeric ? 'text-right' : 'text-left'}`}
                  aria-sort={sort && sort.i === c.i ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" onClick={() => toggleSort(c.i)} data-testid={`source-table-sort-${c.name}`}
                    className="inline-flex items-center gap-1 hover:underline">
                    {c.label}
                    {sort && sort.i === c.i && (sort.dir === 1 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {visible.map((r, n) => (
              <tr key={n} className="hover:bg-surface-sunken">
                {columns.map((c) => (
                  <td key={c.i} className={`px-3 py-1.5 whitespace-nowrap ${c.numeric ? 'text-right font-mono tabular-nums' : 'text-ink'}`}>{r[c.i]}</td>
                ))}
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={columns.length} className="px-3 py-4 text-center text-ink-muted">No lines match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
